import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Form, Select, DatePicker, FormInstance, Spin, Checkbox, Upload, Col, Row, message, Radio, RadioChangeEvent, Space, Divider } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/redux";
import { getCategories, getLocations, getQuestions, getAirports, getSubCategories } from "../../../../redux/features/generaleWebData/generaleWebDataAsyncThunk";
import { FormWrapper } from "../../../../components/styles";
import { capitalizeWord } from "../../../../utils/helpers/capitalizeWord";
import { UploadFile, UploadFileStatus } from "antd/es/upload/interface";
import { imageUploadAccept, validateUploadFiles } from "../../../../utils/helpers/validateUploadFiles";
import { disabledFutureDate } from "../../../../utils/helpers/convertUtcToLocal";
import dayjs, { Dayjs } from "dayjs";
import { preserveOriginalTime } from "../../../../utils/helpers/dateFunctions";
import { fetchQuestions } from "../../../../redux/features/question/questionAsyncThunk";
import { sortOptionsAlphabetically } from "../../../../utils/helpers/sortOptionsAlphabetically";
import { setQuestionData } from "../../../../redux/features/question/questionSlice";
import { getAllCaseManager } from "../../../../redux/features/user/userAsyncThunk";
import { selectUsers, setAdminUsers } from "../../../../redux/features/user/userSlice";
import { RequestResults } from "../../../../redux/common/RequestStatus";
import { DimensionGrid, DimensionGroup, DimensionInputGroup, DimensionLabel, MeasurementUnitGroup, UnitLabel, DimensionsContainer, ContentTextArea } from "../../../ReportDetails/Modal/PrematchStep/styles";
import { StyledPhoneInput } from "../../../ReportDetails/Modal/styles";
import StyledNumberInput from "../../../../components/StyledNumberInput";
import Input, { TextArea } from "../../../../components/Input";
import { convertDimensionsUnits, getErrorMessage, getUnitLabels, validateNumber } from "../../../ReportDetails/utils/formula";
import { Dimensions, FoundItemType, FoundItemTypeOptions, UnitType, UnitTypeOptions } from "../../../../types/FoundItemTypes";

interface CustomUploadFile extends UploadFile<any> {
  binary?: File;
}

interface ItemDetailsFormProps {
  form: FormInstance;
  isEdit: boolean
}

const ItemDetailsForm: React.FC<ItemDetailsFormProps> = ({ form, isEdit }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { data } = useAppSelector((state) => state.foundReport);
  const { data: questions } = useAppSelector((state) => state.question);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [fileList, setFileList] = useState<CustomUploadFile[]>([]);
  const [deletedFileIds, setDeletedFileIds] = useState<string[]>([]);

  const { categoryState, subCategoryState } = useAppSelector((state) => state.generaleWebData);
  const { locationState, airportState } = useAppSelector((state) => state.generaleWebData);
  const { airports, status: airportStatus } = useMemo(() => airportState, [airportState]);
  const { locations, status: locationStatus } = useMemo(() => locationState, [locationState]);
  const { categories, status: categoryStatus } = useMemo(() => categoryState, [categoryState]);
  const { subCategories, status: subCategoryStatus } = useMemo(() => subCategoryState, [subCategoryState]);

  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const [checkedValues, setCheckedValues] = useState<{ [key: string]: boolean | string }>({});
  const { caseManagers, status: adminUsersStatus } = useAppSelector(selectUsers);
  const [dimensions, setDimensions] = useState<Dimensions>();

  const handleSelectChange = (questionId: string, value: string) => {
    setSelectedOptions((prev: any) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleCheckboxChange = (questionId: string, e: any) => {
    setCheckedValues({
      ...checkedValues,
      [questionId]: e.target.checked ? "true" : "false",
    });
  }

  Form.useWatch([], form);
  const foundItemType: FoundItemType = Form.useWatch("foundType", form) || 1;
  const unit: UnitType = Form.useWatch("measurementType", form) || 1;

  const handleFoundItemTypeChange = (e: RadioChangeEvent) => {
    if (!isEdit) {
      form.resetFields();
    }
    form.setFieldValue("foundType", e.target.value);
  };

  const handleUnitChange = async (e: RadioChangeEvent) => {
    const newUnit = e.target.value as UnitType;
    const oldUnit = unit;
    const values = await form.getFieldsValue();

    // Only perform conversion if there are values to convert and units are different
    if (oldUnit !== newUnit) {
      // Get current dimension values from form
      const currentDimensions: Dimensions = {
        length: {
          main: values.length?.main || 0,
          sub: values.length?.sub || 0
        },
        width: {
          main: values.width?.main || 0,
          sub: values.width?.sub || 0
        },
        height: {
          main: values.height?.main || 0,
          sub: values.height?.sub || 0
        },
        weight: {
          main: values.weight?.main || 0,
          sub: values.weight?.sub || 0
        }
      };

      // Convert using the improved convertDimensionsUnits function
      const converted = convertDimensionsUnits(currentDimensions, oldUnit, newUnit);

      if (!converted) return;

      // Update state and form
      setDimensions(converted as Dimensions);

      // Update each dimension field individually to ensure proper form update
      Object.keys(converted).forEach(key => {
        const dimKey = key as keyof Dimensions;
        // Ensure the property exists and has the expected structure
        if (converted[dimKey] && typeof converted[dimKey] === 'object') {
          form.setFieldsValue({
            [dimKey]: {
              main: converted[dimKey]?.main || 0,
              sub: converted[dimKey]?.sub || 0
            }
          });
        }
      });
    }

    form.setFieldValue("measurementType", newUnit);
  };

  useEffect(() => {
    if (airportStatus === "idle" && airports.length === 0) {
      dispatch(getAirports());
    }
  }, [airportStatus, airports, dispatch]);

  useEffect(() => {
    if (locationStatus === "idle" && locations.length === 0) {
      dispatch(getLocations());
    }
  }, [locationStatus, locations, dispatch]);

  const airportsOptions = useMemo(() => {
    return airports.map((airport) => ({
      label: airport.name,
      value: airport.id,
      key: airport.id,
    }));
  }, [airports]);

  const locationsOptions = useMemo(() => {
    return locations.map((location) => ({
      label: location.name,
      value: location.id,
      key: location.id,
    }));
  }, [locations]);

  const selectedLocation = Form.useWatch(["location"], form);
  const selectedSubCategory = Form.useWatch(["subCategory"], form);
  const selectedLocationQuestions = useMemo(() => locations.find((location) => location.id === selectedLocation)?.questions, [selectedLocation, locations]);

  useEffect(() => {
    if (categoryStatus === "idle" && categories.length === 0) {
      dispatch(getCategories());
    }
  }, [dispatch]);

  useEffect(() => {
    if (categoryStatus === "idle" && categories.length === 0) {
      dispatch(getSubCategories(isEdit ? {} : { filterEmpty: 1, status: 1 }));
    }
  }, [subCategoryStatus, dispatch, isEdit]);

  const categoryOptions = useMemo(() => {
    return categories.map((category) => ({
      label: category.name,
      value: category.id,
      key: category.id,
    }));
  }, [categories]);

  const subCategoryOptions = useMemo(() => {
    return subCategories
      .filter((subCategory) => subCategory?.category?.id === form.getFieldValue("category"))
      .map((subCategory) => ({
        label: subCategory.name,
        value: subCategory.id,
        key: subCategory.id,
      }));
  }, [subCategories, form.getFieldValue("category")]);

  const getQuestionsHandle = async (subCategoryId: string) => {
    setQuestionLoading(true);
    const response: any = await dispatch(fetchQuestions({ search_query: '', offset: 0, limit: 1000, subCategoryId }));
    // setQuestions((response?.payload?.data as Question[]) || []);
    setQuestionLoading(false);
  };

  useEffect(() => {
    if (selectedSubCategory) {
      getQuestionsHandle(form.getFieldValue("subCategory"));
    }
  }, [selectedSubCategory]);

  useEffect(() => {
    if (data && isEdit) {
      const itemData = { ...data };
      const locationQuestions: Record<string, string> = {};

      data?.location?.questions?.forEach((question) => {
        locationQuestions[`locationQuestion_${question.id}`] = question.answers?.answer;
      });

      const itemQuestions: Record<string, string> = {};
      data?.subCategory?.questions?.forEach((question) => {
        if (question?.fieldType === 2) {
          itemQuestions[`question_${question.id}`] = (question.answers?.answer === "true").toString() ?? "false";
        } else {
          itemQuestions[`question_${question.id}`] = question.answers?.answer;
        }
      });

      const itemSubQuestions: Record<string, string> = {};
      data?.subCategory?.questions?.forEach((question) => {
        question?.options?.map((option) => {
          option?.subQuestion?.map((subQuestion: any) => {
            if (subQuestion?.fieldType === 2) {
              itemSubQuestions[`subQuestion_${subQuestion?.id}`] = (subQuestion?.answers?.answer === "true")?.toString() || "false";
            } else {
              itemSubQuestions[`subQuestion_${subQuestion?.id}`] = subQuestion?.answers?.answer;
            }
          })
        })
      });

      // Create dimensions object with proper type casting to ensure numeric values
      const newDimensions: Dimensions = {
        length: {
          main: Number(data.lengthPrimaryValue) || 0,
          sub: Number(data.lengthSecondaryValue) || 0
        },
        width: {
          main: Number(data.widthPrimaryValue) || 0,
          sub: Number(data.widthSecondaryValue) || 0
        },
        height: {
          main: Number(data.heightPrimaryValue) || 0,
          sub: Number(data.heightSecondaryValue) || 0
        },
        weight: {
          main: Number(data.weightPrimaryValue) || 0,
          sub: Number(data.weightSecondaryValue) || 0
        }
      };

      setDimensions(newDimensions);

      const formData: { [key: string]: any } = {
        ...itemData,
        caseManager: itemData?.caseManager?.id,
        category: itemData?.category?.id,
        subCategory: itemData?.subCategory?.id,
        foundDate: itemData?.foundDate ? dayjs(itemData?.foundDate) : null,
        offloadDate: itemData?.offloadDate ? dayjs(itemData?.offloadDate) : null,
        airport: itemData?.airport?.id,
        location: itemData?.location?.id,
        ...itemQuestions,
        ...locationQuestions,
        ...itemSubQuestions,
        ...newDimensions
      };

      // Set documents from data if they exist
      if (itemData.documents && itemData.documents.length > 0) {
        const documentFiles = itemData.documents.map((doc: any) => ({
          uid: doc.id?.toString() || `-${Math.random()}`,
          name: doc.documentName || "Document",
          status: "done" as UploadFileStatus,
          url: doc.fullUrl,
          type: "image/jpeg",
        }));
        setFileList(documentFiles);
        formData.documents = documentFiles;
      }

      data?.subCategory?.questions?.forEach((question: any) => {
        if (question.fieldType === 2) {
          formData[`question_${question.id}`] = question.answers?.answer === "true";
        }
        else {
          formData[`question_${question.id}`] = question.answers?.answer;
        }
      });

      form.setFieldsValue(formData);
    }
  }, [data, isEdit]);

  const handleFileChange = async ({ fileList: newFileList }: { fileList: CustomUploadFile[] }) => {
    setFileList(newFileList.slice(0, 3));

    const blobs = newFileList
      .map((file: UploadFile) => {
        if (file.originFileObj) {
          return new Blob([file.originFileObj], { type: file.originFileObj.type });
        }
        return null; // Handle files that do not have originFileObj
      })
      .filter((blob: any) => blob !== null); // Remove null values
    form.setFieldsValue({ documents: blobs });
  };

  const beforeUpload = (file: File) => {
    // check the file type - you can specify the types you'd like here:
    const isImg = file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png" || file.type === "image/gif" || file.type === "image/webp";

    // check the file size - you can specify the file size you'd like here:
    const isLt1M = file.size / 1024 / 1024 <= 1;

    if (!isImg) {
      message.error('You can only upload JPG/JPEG/PNG/GIF/WEBP file formats!');
      return Upload.LIST_IGNORE;
    }

    if (!isLt1M) {
      message.error('Image must be smaller than 1MB!');
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleRemove = (file: CustomUploadFile) => {
    const documentId = data?.documents?.find((doc) => doc.id.toString() === file.uid)?.id;
    if (documentId && file?.uid && data?.id?.length) {
      const currentDeletedFiles = form.getFieldValue('deletedFiles') || [];
      form.setFieldsValue({
        deletedFiles: [...currentDeletedFiles, file.uid]
      });
    }
  };

  const handlePreview = async (file: CustomUploadFile) => {
    if (file.url) {
      window.open(file.url, "_blank");
    }
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    const updatedDate = isEdit && data?.foundDate ? preserveOriginalTime(date, data?.foundDate) : dayjs(date);
    form.setFieldValue('foundDate', updatedDate);
  };

  useEffect(() => {
    if (data && isEdit) {
      // Create an initial checked state for all questions with fieldType 2 (checkbox)
      const initialCheckedValues: { [key: string]: boolean | string } = {};
      const updatedSelectedOptions: { [key: string]: any } = {};

      for (const question of data?.subCategory?.questions) {
        // Set default checked value to false only for fieldType 2 (checkbox)
        if (question.fieldType === 2) {
          initialCheckedValues[question.id] = question.answers?.answer === "true"; // Default to false (unchecked)
        } else if (question.fieldType === 3) {
          const selectedBrand = question?.options?.find(
            (option) => option.name === question?.answers?.answer
          );

          if (selectedBrand) {
            updatedSelectedOptions[question.id] = selectedBrand?.id; // Save dynamic value
          }
        }
      }

      // Update the state once after looping
      setSelectedOptions((prev: any) => ({
        ...prev,
        ...updatedSelectedOptions,
      }));
      setCheckedValues(initialCheckedValues); // Update the state with initial checked values
    } else if (questions && Array.isArray(questions)) {
      // Create an initial checked state for all questions with fieldType 2 (checkbox)
      const initialCheckedValues: { [key: string]: boolean | string } = {};

      for (const question of questions) {
        // Set default checked value to false only for fieldType 2 (checkbox)
        if (question.fieldType === 2) {
          initialCheckedValues[question.id] = question.answers?.answer === "true"; // Default to false (unchecked)
        }
      }

      setCheckedValues(initialCheckedValues); // Update the state with initial checked values
    }
  }, [data, isEdit, questions]);

  const resetQuestionData = async () => {
    await dispatch(setQuestionData([]));
  }

  const onChangeCategory = async () => {
    form.setFieldsValue({ subCategory: null });
    await resetQuestionData();
  }

  const onChangeSubCategory = async () => {
    await resetQuestionData();
  }


  const handleGetAdminUser = async () => {
    await dispatch(
      getAllCaseManager()
    );
  }

  const caseManagerUsers = useMemo(() => {
    if (caseManagers?.length > 0) {
      return caseManagers.map((user) => ({
        value: user?.id,
        label: user?.firstName + " " + user?.lastName,
        key: user?.id
      }))
    }
    return [];
  }, [caseManagers]);

  useEffect(() => {
    handleGetAdminUser();
  }, [dispatch, location?.pathname])

  const handleValueChange = (value: string | number | null, type: "length" | "width" | "height" | "weight", field: "main" | "sub") => {
    const currentValues = {
      main: form.getFieldValue([type, "main"]) || 0,
      sub: form.getFieldValue([type, "sub"]) || 0,
    };

    let newValues = { ...currentValues };

    if (field === "main") {
      const numValue = validateNumber(value);
      newValues.main = numValue !== null ? numValue : 0;
      form.setFieldsValue({
        [type]: newValues,
      });

      // Update dimensions state to keep it in sync with form values
      if (dimensions) {
        setDimensions(prev => {
          // Create a new dimensions object with default values if prev is undefined
          const updatedDimensions = prev || {
            length: { main: 0, sub: 0 },
            width: { main: 0, sub: 0 },
            height: { main: 0, sub: 0 },
            weight: { main: 0, sub: 0 }
          };

          // Update the specific dimension type
          return {
            ...updatedDimensions,
            [type]: {
              ...(updatedDimensions[type] || { main: 0, sub: 0 }),
              main: newValues.main
            }
          };
        });
      }
    } else if (field === "sub") {
      const numValue = validateNumber(value);
      if (numValue !== null) {
        // Simply set the sub value without any conversion
        newValues.sub = numValue;

        form.setFieldsValue({
          [type]: newValues,
        });

        // Update dimensions state to keep it in sync
        if (dimensions) {
          setDimensions(prev => {
            // Create a new dimensions object with default values if prev is undefined
            const updatedDimensions = prev || {
              length: { main: 0, sub: 0 },
              width: { main: 0, sub: 0 },
              height: { main: 0, sub: 0 },
              weight: { main: 0, sub: 0 }
            };

            // Update the specific dimension type
            return {
              ...updatedDimensions,
              [type]: {
                ...(updatedDimensions[type] || { main: 0, sub: 0 }),
                sub: newValues.sub
              }
            };
          });
        }
      }
    }
  }

  const handleSubValueBlur = (type: "length" | "width" | "height" | "weight") => {
    // No conversion logic needed anymore
    // This function is kept for consistency with Step3.tsx and DimensionsTable.tsx
  };

  const renderDimensionInput = (type: "length" | "width" | "height" | "weight") => {
    const [mainUnit, subUnit] = getUnitLabels(type, unit);
    if (!unit) return null;

    const getMainMaxValue = () => {
      // For weight: lb or kg (both 2 digits)
      if (type === "weight") return 99;
      // For dimensions: empty string means it's the sub-unit (cm/in)
      return 0;
    };

    const getSubMaxValue = () => {
      if (type === "weight") return 0; // No sub unit for weight
      return unit === 1 ? 99 : 999; // in: 2 digits (0-99), cm: 3 digits (0-999)
    };

    const validateMainValue = (value: number | null): string | void => {
      if (value === null || value === undefined) return;

      if (value < 0) return "Value cannot be negative";

      // Allow decimal values with up to 2 decimal places
      const decimalPlaces = value.toString().split('.')[1]?.length || 0;
      if (decimalPlaces > 2) return "Maximum 2 decimal places allowed";

      const max = getMainMaxValue();
      if (max > 0 && value > max) return `Value cannot exceed ${max}`;
    };

    const validateSubValue = (value: number | null): string | void => {
      if (value === null || value === undefined) return;

      if (value < 0) return "Value cannot be negative";
      if (value <= 0) return getErrorMessage(type);

      // Allow decimal values with up to 2 decimal places
      const decimalPlaces = value.toString().split('.')[1]?.length || 0;
      if (decimalPlaces > 2) return "Maximum 2 decimal places allowed";

      const max = getSubMaxValue();
      if (value > max) return `Value cannot exceed ${max}`;
    };

    return (
      <DimensionInputGroup>
        <Form.Item
          name={[type, "main"]}
          initialValue={0}
          rules={mainUnit ? [
            {
              validator: async (_, value) => {
                const mainValue = validateNumber(value) ?? 0;
                const subValue = validateNumber(form.getFieldValue([type, "sub"])) ?? 0;

                if (mainValue === 0 && subValue === 0) {
                  throw new Error(getErrorMessage(type));
                }

                const error = validateMainValue(mainValue);
                if (error) throw new Error(error);
              },
            },
          ] : []}
          className="dimension-form-item"
          hidden={!mainUnit}
          validateTrigger={["onChange", "onBlur"]}
        >
          <StyledNumberInput
            size="large"
            min={0}
            max={getMainMaxValue()}
            placeholder="00.00"
            onChange={(value) => handleValueChange(value, type, "main")}
            unit={mainUnit}
            allowDecimals={true}
            onFocus={(e) => {
              const value = form.getFieldValue([type, "main"]);
              if (value < 0.1) {
                form.setFieldsValue({
                  [type]: { main: "", sub: "" },
                });
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name={[type, "sub"]}
          dependencies={[[type, "main"]]}
          initialValue={0}
          rules={subUnit ? [
            {
              validator: async (_, value) => {
                const subValue = validateNumber(value) ?? 0;
                const error = validateSubValue(subValue);
                if (error) throw new Error(error);
              },
            },
          ] : []}
          className="dimension-form-item"
          hidden={!subUnit}
          validateTrigger={["onChange", "onBlur"]}
        >
          <StyledNumberInput
            size="large"
            min={0}
            max={getSubMaxValue()}
            placeholder={unit === 1 ? "00.00" : "000.00"}
            onChange={(value) => handleValueChange(value, type, "sub")}
            onBlur={() => handleSubValueBlur(type)}
            unit={subUnit}
            allowDecimals={true}
            onFocus={(e) => {
              const value = form.getFieldValue([type, "sub"]);
              if (value < 0.1) {
                form.setFieldsValue({
                  [type]: { main: "", sub: "" },
                });
              }
            }}
          />
        </Form.Item>
      </DimensionInputGroup>
    );
  };

  return (
    <FormWrapper form={form} layout="vertical">
      <Input label="" fontWeight="" placeholder="" name="deletedFiles" hidden />

      <Form.Item className="found-form-item-type" name="foundType" initialValue={1} rules={[{ required: true, message: "Please select a found item type!" }]}>
        <Radio.Group
          disabled={isEdit}
          options={[
            {
              label: "Found Item",
              value: 1,
            },
            {
              label: "Identified Found Item",
              value: 2,
            },
          ]}
          size="large"
          onChange={handleFoundItemTypeChange}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={<span style={{ fontWeight: "700" }}>Airport</span>} name="airport" rules={[{ required: true, message: "Please select a airport!" }]}>
            <Select
                size="large"
                options={sortOptionsAlphabetically(airportsOptions, "label")}
                filterOption={(input, option) => (option?.label?.toLowerCase() ?? "").indexOf(input.toLowerCase()) >= 0} loading={airportStatus === "loading"} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<span style={{ fontWeight: "700" }}>Item Category</span>} name="category" rules={[{ required: true, message: "Please select a category!" }]}>
            <Select
              size="large"
              options={sortOptionsAlphabetically(categoryOptions, "label")}
              filterOption={(input, option) => (option?.label?.toLowerCase() ?? "").indexOf(input.toLowerCase()) >= 0}
              loading={categoryStatus === "loading"}
              onChange={onChangeCategory}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item label={<span style={{ fontWeight: "700" }}>Sub Category</span>} name="subCategory" rules={[{ required: true, message: "Please select an subcategory!" }]}>
            <Select size="large" options={sortOptionsAlphabetically(subCategoryOptions, "label")} filterOption={(input, option) => (option?.label?.toLowerCase() ?? "").indexOf(input.toLowerCase()) >= 0} loading={subCategoryStatus === "loading"} onChange={onChangeSubCategory} />
          </Form.Item>
        </Col>
      </Row>

      {questionLoading ? (
        <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Spin />
        </div>
      ) : (
        questions?.length > 0 && (
          <>
            {questions?.map((question) => {
              const fieldName = `question_${question.id}`;
              switch (question.fieldType) {
                case 1:
                  return (
                    <TextArea
                      key={question.id}
                      label={question.fieldName}
                      fontWeight={question.isRequired ? "700" : "400"}
                      placeholder={question.fieldName}
                      name={fieldName}
                      rules={[{ required: question.isRequired, message: "Please enter answer" }]}
                      onChange={(e: any) =>
                        form.setFieldsValue({
                          [fieldName]: e.target.value,
                        })
                      }
                    />
                  );
                case 2:
                  return (
                    <>
                      <Form.Item initialValue={checkedValues[question.id] === "true"}
                        key={question.id} name={fieldName} valuePropName="checked" rules={[{ required: question.isRequired, message: "Please enter answer" }]}>
                        <Checkbox checked={checkedValues[question.id] === "true"} style={{ fontWeight: question.isRequired ? "700" : "400" }} onChange={(e) => handleCheckboxChange(question.id, e)} >{question.fieldName}</Checkbox>
                      </Form.Item>
                      {question?.options && question?.options.length > 0 && (
                        <>
                          {question?.options?.map((option) => (
                            <div style={{ width: "100%" }} key={option.id}>
                              {/* <span>{checkedValues[question.id] && option.name === checkedValues[question.id] && option.name === "true" ? "Yes" : checkedValues[question.id] && option.name === checkedValues[question.id] && option.name === "false" ? "No" : ""}</span> */}
                              {checkedValues[question.id] && option.name === checkedValues[question.id] && option?.subQuestion?.map((subQ: any) => (
                                <TextArea
                                  key={subQ.id}
                                  label={subQ.fieldName}
                                  fontWeight={subQ.isRequired ? "700" : "400"}
                                  name={`subQuestion_${subQ.id}`}
                                  rules={[{ required: subQ.isRequired, message: `Please enter ${subQ.fieldName}` }]}
                                  placeholder={subQ.placeholder}
                                  onChange={(e: any) => form.setFieldsValue({ [`subQuestion_${subQ.id}`]: e.target.value })}
                                />
                              ))}
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  );
                case 3:
                  const questionOptions = question?.options?.map((option) => ({ label: option.name, value: option.id, key: option.name })) || [];
                  const matchingSubQuestions = question?.options?.find((opt) => opt?.id === selectedOptions[question?.id])?.subQuestion || [];

                  return (
                    <>
                      <Form.Item key={question.id} label={<span style={{ fontWeight: question.isRequired ? "700" : "400" }}>{question.fieldName}</span>} name={fieldName} rules={[{ required: question.isRequired, message: "Please enter answer" }]}>
                        <Select
                          size="large"
                          options={sortOptionsAlphabetically(questionOptions, "label")}
                          onSelect={(value) => handleSelectChange(question.id, value)}
                        />
                      </Form.Item>

                      {/* Render subQuestions dynamically if available */}
                      {selectedOptions[question?.id] && matchingSubQuestions?.length > 0 && matchingSubQuestions?.map((subQ: any) => (
                        <TextArea
                          key={subQ.id}
                          label={subQ.fieldName}
                          fontWeight={subQ.isRequired ? "700" : "400"}
                          name={`subQuestion_${subQ.id}`}
                          rules={[{ required: subQ.isRequired, message: `Please enter ${subQ.fieldName}` }]}
                          placeholder={subQ.placeholder}
                          onChange={(e: any) => form.setFieldsValue({ [`subQuestion_${subQ.id}`]: e.target.value })}
                        />
                      ))}
                    </>
                  );
                default:
                  return null;
              }
            })}
          </>
        )
      )}

      <TextArea
        label="Item Description"
        fontWeight="700"
        name="itemDescription"
        rules={[{ required: true, message: `Please enter Item Description!` }]}
        placeholder="Item Description"
        onBlur={(e: any) => {
          const capitalizedValue = capitalizeWord(e.target.value);
          form.setFieldsValue({ itemDescription: capitalizedValue });
        }}
      />

      <TextArea
        label="Remarks"
        fontWeight="400"
        name="remarks"
        placeholder="Remarks"
        onBlur={(e: any) => {
          const capitalizedValue = capitalizeWord(e.target.value);
          form.setFieldsValue({ remarks: capitalizedValue });
        }}
      />

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item label={<span style={{ fontWeight: "700" }}>Location</span>} name="location" rules={[{ required: true, message: "Please select a location!" }]}>
            <Select size="large" options={sortOptionsAlphabetically(locationsOptions, 'label')} filterOption={(input, option) => (option?.label?.toLowerCase() ?? "").indexOf(input.toLowerCase()) >= 0} loading={locationStatus === "loading"} />
          </Form.Item>
        </Col>
      </Row>
      {selectedLocationQuestions?.map((question) => (
        <Input placeholder={question.placeholder} fontWeight={question.isRequired ? "700" : "400"} label={question.fieldName} name={`locationQuestion_${question.id}`} rules={[{ required: question.isRequired, message: "Please enter a location question!" }]} />
      ))}

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={<span style={{ fontWeight: "700" }}>Found Date</span>}
            getValueProps={(value) => ({
              value: value ? dayjs(value) : undefined,
            })}
            name="foundDate"
            rules={[{ required: true, message: "Please enter Found Date!" }]}
          >
            <DatePicker
              style={{ width: "100%" }} format="MM/DD/YYYY" disabledDate={disabledFutureDate} placeholder="Select Found Date" size="large" onChange={handleDateChange} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Case Manager" name="caseManager" rules={[{ required: false, message: "Please select a case manager!" }]}>
            <Select allowClear size="large" placeholder="Select Case Manager" options={caseManagerUsers} filterOption={(input, option) => (option?.label?.toLowerCase() ?? "").indexOf(input.toLowerCase()) >= 0} loading={adminUsersStatus === RequestResults?.pending} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Item images" name="documents" rules={[{ validator: validateUploadFiles(form) }]}>
        <Upload
          listType="picture-card"
          fileList={fileList}
          onChange={handleFileChange}
          multiple
          beforeUpload={beforeUpload}
          maxCount={3}
          onPreview={handlePreview}
          showUploadList={{
            showPreviewIcon: true,
            showRemoveIcon: true,
            showDownloadIcon: false,
          }}
          onRemove={handleRemove}
          accept={imageUploadAccept}
        >
          {fileList.length < 3 && "+ Upload"}
        </Upload>
      </Form.Item>

      {FoundItemTypeOptions.FoundLuggage === foundItemType &&
        <>
          <Divider />
          <MeasurementUnitGroup>
            <UnitLabel>Measurement Unit</UnitLabel>
            <Space size={24}>
              <Form.Item initialValue={1} className="measurement-unit-form-item" name="measurementType" rules={[{ required: true, message: "Please select a measurement unit" }]}>
                <Radio.Group
                  options={[
                    {
                      label: "inches/lb",
                      value: 1,
                    },
                    {
                      label: "cm/kg",
                      value: 2,
                    },
                  ]}
                  size="large"
                  onChange={handleUnitChange}
                />
              </Form.Item>
            </Space>
          </MeasurementUnitGroup>

          <DimensionGrid>
            <DimensionGroup>
              <DimensionLabel required>Length</DimensionLabel>
              {renderDimensionInput("length")}
            </DimensionGroup>

            <DimensionGroup>
              <DimensionLabel required>Width</DimensionLabel>
              {renderDimensionInput("width")}
            </DimensionGroup>

            <DimensionGroup>
              <DimensionLabel required>Height</DimensionLabel>
              {renderDimensionInput("height")}
            </DimensionGroup>

            <DimensionGroup>
              <DimensionLabel required>Weight</DimensionLabel>
              {renderDimensionInput("weight")}
            </DimensionGroup>
          </DimensionGrid>

          <Form.Item name="description" label={<DimensionLabel isNoMarginBottom={true}>Contents</DimensionLabel>} rules={[{ required: true, message: "Please enter contents" }]}>
            <ContentTextArea placeholder="Contents" />
          </Form.Item>
          <Divider />

          <Row gutter={16}>
            <Col span={12}>
              <Input fontWeight="700" label="Passenger Name" placeholder="" name="passengerName" rules={[{ required: true, message: "Please enter a passenger name!" }]} />
            </Col>
            <Col span={12} md={12}>
              <Input fontWeight="400" label="Additional Name" name="passengerName2" placeholder="" rules={[{ required: false, message: "Please enter a additional name!" }]} />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={<span style={{ fontWeight: "700" }}>Passenger Phone</span>} name="passengerPhone" rules={[{ required: true, message: "Please enter a passenger phone!" }]}>
                <StyledPhoneInput
                  country={"us"}
                  inputProps={{
                    placeholder: "Enter a Phone Number",
                  }}
                  inputStyle={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "3px",
                    fontSize: "15px",
                    paddingLeft: "40px",
                    background: "#fff",
                    height: "40px",
                  }}
                  buttonStyle={{
                    borderRight: "none",
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12} md={12}>
              <Form.Item label="Additional Phone" name="passengerPhone2" rules={[{ required: false, message: "Please enter a additional phone!" }]}>
                <StyledPhoneInput
                  country={"us"}
                  inputProps={{
                    placeholder: "Enter a Phone Number",
                  }}
                  inputStyle={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "3px",
                    fontSize: "15px",
                    paddingLeft: "40px",
                    background: "#fff",
                    height: "40px",
                  }}
                  buttonStyle={{
                    borderRight: "none",
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Input fontWeight="700" label="Passenger Email" placeholder="" name="passengerEmail" rules={[{ required: true, message: "Please enter a passenger email!" }, { type: "email", message: "Please enter a valid email address" }]} />
            </Col>
            <Col span={12} md={12}>
              <Input fontWeight="400" label="Additional Email" name="passengerEmail2" placeholder="" rules={[{ required: false, message: "Please enter a additional email!" }, { type: "email", message: "Please enter a valid additional email address" }, {
                validator: (_: any, value: string) => {
                  const email = form.getFieldValue("passengerEmail");
                  if (value && value === email) {
                    return Promise.reject(new Error("Additional email cannot be same as email"));
                  }
                  return Promise.resolve();
                },
              }]} />
            </Col>
          </Row>
        </>
      }

    </FormWrapper>
  );
};

export default ItemDetailsForm;
